/**
 * Bond Mentor — Yield Curve Data (Drop 2)
 *
 * Contains yield data across all maturities, historical comparison curves,
 * curve shape analysis, spread calculations, and Fed Watch data.
 * In future drops, this data feeds from FRED and Treasury.gov APIs.
 */

// ============================================
// MATURITY LABELS (x-axis of yield curve)
// ============================================
export const MATURITIES = [
  { key: '1m', label: '1M', months: 1 },
  { key: '3m', label: '3M', months: 3 },
  { key: '6m', label: '6M', months: 6 },
  { key: '1y', label: '1Y', months: 12 },
  { key: '2y', label: '2Y', months: 24 },
  { key: '3y', label: '3Y', months: 36 },
  { key: '5y', label: '5Y', months: 60 },
  { key: '7y', label: '7Y', months: 84 },
  { key: '10y', label: '10Y', months: 120 },
  { key: '20y', label: '20Y', months: 240 },
  { key: '30y', label: '30Y', months: 360 },
];

// ============================================
// CURVE SHAPES — What they mean
// ============================================
export const CURVE_SHAPES = {
  NORMAL: {
    key: 'NORMAL',
    label: 'Normal',
    emoji: '📈',
    color: '#22c55e',
    signal: 'GREEN',
    shortDesc: 'Healthy & Upward Sloping',
    description: 'Long-term bonds pay more than short-term bonds. This is the healthy, natural state. It means investors expect the economy to grow and inflation to remain manageable. Historically, stocks perform well in this environment.',
    actionHint: 'This is a green light for risk assets. Stay invested.',
  },
  FLATTENING: {
    key: 'FLATTENING',
    label: 'Flattening',
    emoji: '📊',
    color: '#a3e635',
    signal: 'YELLOW',
    shortDesc: 'Losing Steepness — Watch Closely',
    description: 'The gap between short and long-term rates is shrinking. Short-term rates are rising faster than long-term rates, or long-term rates are falling. The economy may be losing momentum. This is the first warning stage.',
    actionHint: 'Pay attention. Not yet alarming, but the bond market is telling you growth expectations are cooling.',
  },
  FLAT: {
    key: 'FLAT',
    label: 'Flat',
    emoji: '➡️',
    color: '#eab308',
    signal: 'YELLOW',
    shortDesc: 'Short & Long Rates Nearly Equal',
    description: 'Short-term and long-term bonds pay almost the same. This is unusual and historically precedes either inversion or a return to normal. The economy is at a crossroads. Market uncertainty is high.',
    actionHint: 'Caution warranted. Tighten stops. Reduce speculative positions. Watch daily for which direction the curve breaks.',
  },
  INVERTING: {
    key: 'INVERTING',
    label: 'Inverting',
    emoji: '⚠️',
    color: '#f97316',
    signal: 'RED',
    shortDesc: 'Beginning to Flip — Recession Warning',
    description: 'Parts of the curve have inverted — some short-term bonds now pay MORE than longer-term bonds. This is the bond market\'s way of saying it expects economic trouble ahead. Not all maturities are inverted yet, but the process has begun.',
    actionHint: 'Reduce risk exposure. This signal has preceded every modern recession. Take it seriously.',
  },
  INVERTED: {
    key: 'INVERTED',
    label: 'Inverted',
    emoji: '🔻',
    color: '#ef4444',
    signal: 'RED',
    shortDesc: 'Fully Flipped — Recession Highly Likely',
    description: 'Short-term bonds pay significantly more than long-term bonds across multiple maturities. This is the most reliable recession predictor in finance. Every U.S. recession in modern history was preceded by a yield curve inversion. The bond market is screaming that trouble is coming.',
    actionHint: 'Deleverage significantly. Move to defensive positions. The clock is ticking — recessions typically start 6-18 months after a deep inversion.',
  },
  STEEPENING_POST_INVERSION: {
    key: 'STEEPENING_POST_INVERSION',
    label: 'Steepening After Inversion',
    emoji: '🚨',
    color: '#dc2626',
    signal: 'RED',
    shortDesc: 'THE Most Dangerous Phase',
    description: 'The curve was inverted and is now steepening back toward normal. Most people think this means the danger has passed — they are WRONG. This is actually when the recession typically HITS. The curve steepens because the Fed starts cutting short-term rates in response to economic weakness, but the damage is already done. This is the phase most investors miss.',
    actionHint: 'MAXIMUM CAUTION. This is historically the most dangerous point in the entire cycle. Do not be fooled by the curve "normalizing." The recession is likely arriving NOW.',
  },
};

// ============================================
// YIELD CURVE DATA PER SCENARIO
// ============================================
export const YIELD_CURVE_SCENARIOS = {
  ALL_CLEAR: {
    curveShape: 'NORMAL',
    // Yields at each maturity point — ACTUAL as of 03-26-2026
    yields: {
      '1m': 4.33, '3m': 4.30, '6m': 4.18, '1y': 4.05,
      '2y': 3.94, '3y': 3.95, '5y': 4.02, '7y': 4.15,
      '10y': 4.39, '20y': 4.72, '30y': 4.68,
    },
    // Key spreads — ACTUAL
    spreads: {
      twoTen: 0.45,        // 10Y minus 2Y — healthy positive slope
      threeMonthTen: 0.09,  // 10Y minus 3M
      twoThirty: 0.74,      // 30Y minus 2Y
      fiveThirty: 0.66,     // 30Y minus 5Y
    },
    weeklyChange: {
      twoTen: +0.03,
      direction: 'Stable — curve maintaining healthy slope',
    },
    monthlyChange: {
      twoTen: +0.08,
      direction: 'Gradually steepening — post-easing normalization',
    },
    curveAnalysis: 'The yield curve is in a healthy, normal shape with a +45 basis point spread between 2-year and 10-year Treasuries. The Fed\'s 2025 easing cycle (cutting from 5.25% down to 3.50-3.75%) has allowed the curve to steepen naturally. Long-term rates are elevated at 4.39% (10Y) driven by inflation concerns from the U.S.-Iran conflict and continued deficit spending. The curve shape is positive — no recession signal from this angle.',
  },

  EARLY_WARNING: {
    curveShape: 'FLAT',
    yields: {
      '1m': 4.52, '3m': 4.55, '6m': 4.53, '1y': 4.50,
      '2y': 4.50, '3y': 4.48, '5y': 4.45, '7y': 4.48,
      '10y': 4.55, '20y': 4.62, '30y': 4.65,
    },
    spreads: {
      twoTen: 0.05,
      threeMonthTen: 0.00,
      twoThirty: 0.15,
      fiveThirty: 0.20,
    },
    weeklyChange: {
      twoTen: -0.08,
      direction: 'Flattening — short rates catching up to long rates',
    },
    monthlyChange: {
      twoTen: -0.22,
      direction: 'Significant flattening trend — headed toward inversion',
    },
    curveAnalysis: 'The yield curve has flattened significantly. The 2-year and 10-year yields are nearly identical, with only a 5 basis point gap. The 3-month to 10-year spread has gone to zero. This means the bond market sees very little economic growth ahead. The flattening trend over the past month is concerning — the curve is compressing fast. If this continues, inversion could happen within weeks.',
  },

  DANGER_ZONE: {
    curveShape: 'INVERTED',
    yields: {
      '1m': 5.30, '3m': 5.40, '6m': 5.45, '1y': 5.50,
      '2y': 5.55, '3y': 5.42, '5y': 5.28, '7y': 5.22,
      '10y': 5.20, '20y': 5.30, '30y': 5.35,
    },
    spreads: {
      twoTen: -0.35,
      threeMonthTen: -0.20,
      twoThirty: -0.20,
      fiveThirty: 0.07,
    },
    weeklyChange: {
      twoTen: -0.05,
      direction: 'Deepening inversion — getting worse',
    },
    monthlyChange: {
      twoTen: -0.18,
      direction: 'Rapid inversion deepening — strong recession signal',
    },
    curveAnalysis: 'The yield curve is deeply inverted. The 2-year Treasury pays 5.55% while the 10-year only pays 5.20% — a -35 basis point inversion. Short-term bonds pay MORE than long-term bonds across most of the curve. This is the bond market\'s loudest recession alarm. Every U.S. recession in modern history was preceded by an inversion of this magnitude. The inversion has been deepening over the past month, meaning conditions are getting worse, not better. Smart money is positioned for a significant economic downturn.',
  },
};

// ============================================
// HISTORICAL COMPARISON CURVES
// ============================================
export const HISTORICAL_CURVES = {
  PRE_2008: {
    label: 'Pre-2008 Crisis (Jan 2007)',
    color: '#f97316',
    description: 'Inverted curve before the Great Financial Crisis. Stocks peaked Oct 2007, recession began Dec 2007.',
    yields: {
      '1m': 5.10, '3m': 5.11, '6m': 5.15, '1y': 5.04,
      '2y': 4.82, '3y': 4.72, '5y': 4.69, '7y': 4.72,
      '10y': 4.76, '20y': 4.96, '30y': 4.90,
    },
  },
  PRE_2020: {
    label: 'Pre-COVID Signal (Aug 2019)',
    color: '#a855f7',
    description: 'Curve inverted briefly. Recession began Feb 2020 (COVID accelerated what the curve predicted).',
    yields: {
      '1m': 2.12, '3m': 2.00, '6m': 1.92, '1y': 1.76,
      '2y': 1.50, '3y': 1.44, '5y': 1.41, '7y': 1.51,
      '10y': 1.53, '20y': 1.84, '30y': 1.98,
    },
  },
  DOTCOM: {
    label: 'Dot-Com Peak (Mar 2000)',
    color: '#06b6d4',
    description: 'Inverted curve before the dot-com bust. Nasdaq peaked the same month. Recession began Mar 2001.',
    yields: {
      '1m': 5.65, '3m': 5.87, '6m': 6.14, '1y': 6.22,
      '2y': 6.52, '3y': 6.41, '5y': 6.28, '7y': 6.19,
      '10y': 6.11, '20y': 6.20, '30y': 5.85,
    },
  },
};

// ============================================
// FED WATCH DATA PER SCENARIO
// ============================================
export const FED_WATCH_SCENARIOS = {
  ALL_CLEAR: {
    fedFundsRate: 3.625,
    fedFundsRange: '3.50% - 3.75%',
    fedPosture: 'NEUTRAL',
    postureLabel: 'On Hold After Easing',
    postureColor: '#3b82f6',
    postureEmoji: '⏸️',
    marketExpects: 'Holding steady. No further cuts expected near-term due to inflation uptick from geopolitical tensions.',
    fedSays: 'The economy remains resilient. Inflation has ticked up due to supply disruptions but we expect it to moderate. We are well-positioned to respond in either direction.',
    bondsSay: 'The Fed is done cutting for now. Elevated 10Y yields (4.39%) reflect inflation concern, not recession fear. Curve is healthy. Rate stance is appropriate.',
    disagreementLevel: 18,
    disagreementNote: 'The Fed and bond market are broadly in agreement. The curve\'s positive slope confirms the market accepts the current rate stance. Mild tension from geopolitical inflation uncertainty, but no significant disconnect.',
    realRate: 1.00,
    realRateZone: 'NEUTRAL',
    realRateNote: 'Near neutral — the Fed\'s easing cycle brought rates down significantly from 5.25%. The current real rate is no longer restrictive, supporting growth while keeping inflation expectations anchored.',
    balanceSheet: {
      direction: 'CONTRACTING',
      monthlyChange: -35,
      label: 'Slowed Runoff (QT Tapering)',
      note: 'The Fed has slowed its balance sheet reduction from $60B/month to $35B/month. This measured approach supports market liquidity while continuing to normalize the balance sheet.',
    },
    dotPlot: {
      medianYear1: 3.50,
      medianYear2: 3.25,
      note: 'Fed officials project rates staying near current levels through 2026, with potential for modest additional cuts in 2027 if inflation returns to target.',
    },
  },

  EARLY_WARNING: {
    fedFundsRate: 4.50,
    fedFundsRange: '4.25% - 4.50%',
    fedPosture: 'HAWKISH',
    postureLabel: 'Leaning Hawkish',
    postureColor: '#f97316',
    postureEmoji: '🦅',
    marketExpects: 'No cuts this year, possible HIKE if inflation re-accelerates',
    fedSays: 'Inflation has been sticky. We will keep rates elevated for as long as necessary. Additional tightening is not off the table.',
    bondsSay: 'The Fed may be too tight. The flattening curve suggests growth is slowing faster than the Fed admits.',
    disagreementLevel: 55,
    disagreementNote: 'IMPORTANT: The bond market and the Fed disagree. The Fed sounds hawkish, but the flattening curve says the economy is weakening. Historically, the bond market is right more often than the Fed.',
    realRate: 2.15,
    realRateZone: 'POSITIVE',
    realRateNote: 'Clearly restrictive. The real rate is high enough to slow economic activity. This acts like a headwind for stocks and business investment.',
    balanceSheet: {
      direction: 'CONTRACTING',
      monthlyChange: -60,
      label: 'Accelerated Runoff',
      note: 'The Fed increased the pace of balance sheet reduction. This drains liquidity from the financial system faster. Combined with high rates, this is a double-tightening squeeze.',
    },
    dotPlot: {
      medianYear1: 4.50,
      medianYear2: 4.00,
      note: 'Fed officials revised projections upward — fewer cuts expected. Some officials see NO cuts this year.',
    },
  },

  DANGER_ZONE: {
    fedFundsRate: 5.25,
    fedFundsRange: '5.00% - 5.25%',
    fedPosture: 'BEHIND_THE_CURVE',
    postureLabel: 'Behind the Curve',
    postureColor: '#ef4444',
    postureEmoji: '🚨',
    marketExpects: 'Emergency cuts needed — Fed is too slow to respond to deteriorating conditions',
    fedSays: 'We remain committed to price stability. The labor market is still strong. We see no reason for imminent rate adjustments.',
    bondsSay: 'The Fed is dangerously wrong. The deeply inverted curve, widening credit spreads, and collapsing forward rates all scream recession. The Fed needs to cut NOW, and the longer they wait, the worse the eventual downturn.',
    disagreementLevel: 88,
    disagreementNote: 'CRITICAL DISCONNECT: The bond market and the Fed are in extreme disagreement. The Fed says everything is fine. The bond market says a recession is coming. This level of disagreement has preceded every major market downturn in the last 30 years. TRUST THE BONDS.',
    realRate: 2.85,
    realRateZone: 'SHARPLY_POSITIVE',
    realRateNote: 'Dangerously restrictive. This level of real rates is actively crushing economic activity. Businesses cannot afford to borrow. Consumer spending is contracting. The vice is tightening.',
    balanceSheet: {
      direction: 'CONTRACTING',
      monthlyChange: -60,
      label: 'Full-Speed Runoff',
      note: 'The Fed is still draining liquidity even as the economy deteriorates. This amplifies the damage from high rates. The combination is suffocating credit markets.',
    },
    dotPlot: {
      medianYear1: 4.75,
      medianYear2: 3.75,
      note: 'Fed officials finally acknowledge cuts may be needed, but their projected pace is far too slow compared to what the bond market is pricing.',
    },
  },
};

// ============================================
// REAL RATE ZONES
// ============================================
export const REAL_RATE_ZONES = {
  DEEPLY_NEGATIVE: {
    label: 'Deeply Negative',
    color: '#22c55e',
    range: 'Below -1.0%',
    meaning: 'Very loose conditions. Borrowing is essentially free after inflation. Great for stocks short-term, but creates inflation risk long-term.',
  },
  SLIGHTLY_NEGATIVE: {
    label: 'Slightly Negative',
    color: '#a3e635',
    range: '-1.0% to 0%',
    meaning: 'Accommodative. The Fed is still supporting growth. Generally positive for stocks.',
  },
  NEUTRAL: {
    label: 'Neutral',
    color: '#3b82f6',
    range: '0% to 1.0%',
    meaning: 'Balanced. Neither helping nor hurting the economy. The Goldilocks zone.',
  },
  SLIGHTLY_POSITIVE: {
    label: 'Slightly Positive',
    color: '#eab308',
    range: '1.0% to 2.0%',
    meaning: 'Mildly restrictive. Enough to slow inflation without crushing growth. Keep an eye on it.',
  },
  POSITIVE: {
    label: 'Positive',
    color: '#f97316',
    range: '2.0% to 2.5%',
    meaning: 'Clearly restrictive. This is a headwind for stocks and business investment. Growth is being actively slowed.',
  },
  SHARPLY_POSITIVE: {
    label: 'Sharply Positive',
    color: '#ef4444',
    range: 'Above 2.5%',
    meaning: 'Dangerously restrictive. Economic activity is being crushed. Recession risk is elevated. Historically this precedes significant stock market declines.',
  },
};

// ============================================
// FED POSTURE TYPES
// ============================================
export const FED_POSTURES = {
  DOVISH: { label: 'Dovish (Easing)', emoji: '🕊️', color: '#22c55e', meaning: 'The Fed is cutting rates or signaling cuts. Supportive for stocks.' },
  NEUTRAL: { label: 'Neutral (On Hold)', emoji: '⏸️', color: '#3b82f6', meaning: 'The Fed is holding steady. Neither helping nor hurting.' },
  HAWKISH: { label: 'Hawkish (Tightening)', emoji: '🦅', color: '#f97316', meaning: 'The Fed is raising rates or signaling rate hikes. Headwind for stocks.' },
  BEHIND_THE_CURVE: { label: 'Behind the Curve', emoji: '🚨', color: '#ef4444', meaning: 'The Fed is responding too slowly to deteriorating conditions. The bond market has lost confidence. Major risk.' },
};
